import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { filter, startWith, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NavBarItem } from '@shared/models/nav-bar-item.model';
import { DrawerPosition, DrawerType, SectionState } from '@shared-features/enums/layout.enum';
import { LayoutService } from '@layout/services/layout.service';
import { InformationDialogComponent } from '@shared-features/components/information-dialog/information-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CustomerAPI } from '@features/customers/services/customer-api';
import { UserApi } from '@shared-features/services';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { Customer } from '@features/customers/models/customer.model';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums';
import { DrawerComponent } from '@shared/components/drawer/drawer.component';
import { BidChatDrawerService } from '@features/bid-chat/services/bid-chat-drawer.service';
import { BidChatService } from '@features/bid-chat/services/bid-chat.service';
import { BID_CHAT_PUSH } from '@features/bid-chat/constants/bid-chat.constants';

@Component({
  selector: 'app-main-page',
  templateUrl: './main-page.component.html',
  styleUrls: ['./main-page.component.scss'],
})
export class MainPageComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() navItems: NavBarItem[];

  @ViewChild('notices') noticesRef: ElementRef<HTMLElement>;
  @ViewChild('drawer', { static: true }) drawerRef: DrawerComponent;

  sectionState = SectionState.Ready;
  drawerPosition: DrawerPosition = DrawerPosition.End;
  drawerType: DrawerType = null;
  DrawerType = DrawerType;
  drawerOpened = false;
  private drawerCloseTimer?: ReturnType<typeof setTimeout>;
  navbarCollapsed = false;
  destroy$ = new Subject();
  mobileBreakpoint = 960;
  openMenu = false;
  customerData: Customer;
  showInfoBanner = false;
  isBiddingTemplate = false;

  private noticesObserver: ResizeObserver;

  constructor(
    private layoutService: LayoutService,
    private router: Router,
    private matDialog: MatDialog,
    private customerAPI: CustomerAPI,
    private userApi: UserApi,
    private elementRef: ElementRef<HTMLElement>,
    private bidChatDrawerService: BidChatDrawerService,
    private bidChatService: BidChatService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isBiddingTemplate = (this.userApi.currentUser$.value?.creationTemplates || [])?.some(
      (template) => template?.type === ShipmentCreationTemplateTypes.Bidding
    );
    this.getCustomer();
    this.followBidChatDrawer();
    this.openChatFromLink();

    this.router.events
      .pipe(
        filter((event) => event instanceof RouteConfigLoadStart || event instanceof RouteConfigLoadEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        if (event instanceof RouteConfigLoadStart) {
          this.sectionState = SectionState.Loading;
        } else {
          this.sectionState = SectionState.Ready;
        }
      });

    if (window.innerWidth < this.mobileBreakpoint) {
      this.navbarCollapsed = false;
    }
  }

  ngAfterViewInit(): void {
    // The banners are only offset below the routed page's fixed breadcrumb bar while they
    // actually render something; an empty stack must not push the page down. Whether it is
    // empty changes at runtime (banners appear and get dismissed), hence the observer.
    const notices = this.noticesRef?.nativeElement;
    if (!notices || typeof ResizeObserver === 'undefined') return;

    this.noticesObserver = new ResizeObserver(() => this.updateNoticesOffset());
    this.noticesObserver.observe(notices);
  }

  ngOnDestroy(): void {
    this.noticesObserver?.disconnect();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private updateNoticesOffset(): void {
    const hasNotices = (this.noticesRef?.nativeElement?.offsetHeight ?? 0) > 0;
    const style = this.elementRef.nativeElement.style;

    // `--page-header-height` is published by the routed page; it resolves to 0 on pages
    // that render no breadcrumb bar, so the banners sit straight under the header there.
    style.setProperty('--layout-notices-offset', hasNotices ? 'var(--page-header-height, 0px)' : '0px');
    // the banners now reserve the bar's space, so the routed page must not reserve it too
    style.setProperty('--page-header-spacer', hasNotices ? '0px' : '');
  }

  onDrawerOpened(event): void {
    // Reopened right after a close: the pending "closed" must not collapse the new drawer.
    clearTimeout(this.drawerCloseTimer);
    this.drawerType = event.type;
    if (event.open) {
      this.drawerOpened = event.open;
      this.drawerPosition = DrawerPosition.End;
    }
  }

  /** The bid chat (#47189) is opened from the header and the bids list through its service. */
  private followBidChatDrawer(): void {
    this.bidChatDrawerService.view$.pipe(takeUntil(this.destroy$)).subscribe((view) => {
      const isChatShown = this.drawerType === DrawerType.Chat;
      if (view && !isChatShown) {
        // Another panel (notifications, filters) is open: swap its content, as a toggle would shut it.
        const isDrawerOpen = !!this.drawerRef?.drawer?.opened;
        this.onDrawerOpened({ open: true, type: DrawerType.Chat });
        if (!isDrawerOpen) {
          this.drawerRef?.toggleDrawer();
        }
      } else if (!view && isChatShown) {
        this.drawerRef?.toggleDrawer();
        this.onDrawerClosed();
      }
    });

    // Escape closes the drawer by itself, past app-drawer's close button and backdrop: tell the chat,
    // or its view (and live listeners) would stay behind a closed drawer.
    this.drawerRef?.drawer?.closedStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.drawerType === DrawerType.Chat) {
        this.onDrawerClosed();
      }
    });
  }

  /**
   * A chat push clicked while the tab was in the background opens the app on its link
   * (`?openChat=<conversation id>&chatShipmentId=<id>`, see `BID_CHAT_PUSH`): open that chat, then
   * drop the params so a reload or Back doesn't open it again. The params go for everyone; only
   * customers with Bidding, who have the chat, get it opened.
   */
  private openChatFromLink(): void {
    let handledLink: string | null = null;
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const url = this.router.parseUrl(this.router.url);
        const conversationId = url.queryParamMap.get(BID_CHAT_PUSH.linkConversationParam);
        const shipmentId = Number(url.queryParamMap.get(BID_CHAT_PUSH.linkShipmentParam)) || null;
        if (!conversationId && !shipmentId) return;
        // The first load's own NavigationEnd can still show the params the replace below drops.
        const link = `${conversationId}|${shipmentId}`;
        if (link === handledLink) return;
        handledLink = link;
        delete url.queryParams[BID_CHAT_PUSH.linkConversationParam];
        delete url.queryParams[BID_CHAT_PUSH.linkShipmentParam];
        this.router.navigateByUrl(url, { replaceUrl: true });
        if (this.isBiddingTemplate) {
          this.bidChatService.openChat({ conversationId, shipmentId });
        }
      });
  }

  onDrawerClosed(): void {
    // Closed by the backdrop: the chat's service must forget its view too.
    if (this.drawerType === DrawerType.Chat) {
      this.drawerType = null;
      this.bidChatDrawerService.close();
    }

    if (this.layoutService.websiteDirection$.value === 'rtl') {
      this.drawerOpened = false;
    } else {
      this.drawerCloseTimer = setTimeout(() => {
        this.drawerOpened = false;
      }, 250);
    }

    this.drawerType = null;
  }

  onNavbarCollapseChange(event: boolean): void {
    this.navbarCollapsed = event;
  }

  handelOpenMenu(event) {
    this.openMenu = event;
  }

  getCustomer() {
    const userId = this.userApi.currentUser$.value?.parentId;
    if(!userId) return;
      this.load(this.customerAPI.getCustomerById(userId.toString()), { isLoadingTransparent: true }).subscribe((res) => {
      this.customerData = res.customer;
      this.userApi.currentUser$.value.title = this.customerData?.title;
      this.userApi.reSaveUserInStorage(this.userApi.currentUser$.value);
      this.checkMissingData();
    });
  }

  isFieldEmpty(value: string | number): boolean {
    if (!value) return true;
    const stringValue = value.toString().trim();
    return stringValue === '' || stringValue.startsWith('customer_');
  }

  checkMissingData() {
    const { vatNumber, title, crNumber } = this.customerData || {};
    const hasMissingData = this.isFieldEmpty(vatNumber) || this.isFieldEmpty(title) || this.isFieldEmpty(crNumber);
    this.showInfoBanner = hasMissingData;
  }

  openInformationDialog() {
    const { vatNumber, title, crNumber } = this.customerData || {};

    const dialogRef = this.matDialog.open(InformationDialogComponent, {
      data: {
        id: this.customerData?.id,
        vatNumber: vatNumber,
        title: title?.startsWith('customer_') ? '' : this.customerData?.title,
        crNumber: crNumber,
      },
      width: '530px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.customerData = result;
        this.checkMissingData();
      }
    });
  }
}
